import { Baby, ArrowLeft, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@parentpath/i18n';

interface ChildNumberStepProps {
  value: number;
  onChange: (num: number) => void;
  onNext: () => void;
  onBack: () => void;
}

export function ChildNumberStep({ value, onChange, onNext, onBack }: ChildNumberStepProps) {
  const { t } = useTranslation('onboarding');

  const options = [
    { value: 1, label: t('childNumber.first') },
    { value: 2, label: t('childNumber.second') },
    { value: 3, label: t('childNumber.third') },
    { value: 4, label: t('childNumber.fourth') },
  ];

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-white/60 hover:text-white mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('common.back')}
      </button>

      {/* Icon */}
      <div className="w-16 h-16 mx-auto mb-6 bg-pp-orange/20 rounded-full flex items-center justify-center">
        <Baby className="w-8 h-8 text-pp-orange" />
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold text-white mb-2 text-center">
        {t('childNumber.title')}
      </h1>

      {/* Description */}
      <p className="text-white/70 mb-6 text-center">
        {t('childNumber.description')}
      </p>

      {/* Options */}
      <div className="space-y-3 mb-8">
        {options.map((option) => (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            className={cn(
              'w-full flex items-center justify-between p-4 rounded-[10px] border-2 transition-all',
              value === option.value
                ? 'border-pp-orange bg-pp-orange/20'
                : 'border-white/20 bg-white/5 hover:border-white/30'
            )}
          >
            <span className={cn(
              'font-medium',
              value === option.value ? 'text-white' : 'text-white/90'
            )}>
              {option.label}
            </span>
            {value === option.value && (
              <div className="w-6 h-6 bg-pp-orange rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </button>
        ))}
      </div>

      {/* CTA */}
      <button
        onClick={onNext}
        className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors"
      >
        {t('common.next')}
      </button>
    </div>
  );
}
